/**
 * PortfolioSelectorModal - Presentation Layer
 * 
 * Simple modal to select which portfolio to add an opportunity to.
 * Used when adding from the map (where portfolio context isn't set).
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  Modal,
  StyleSheet,
  Pressable,
  ActivityIndicator,
  FlatList,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { color, text, space, radius, icon, touch } from '../../../shared/tokens/mobile';
import { authApi } from '../../../shared/utils/api';
import { Portfolio } from '../../../shared/types/portfolio';
import { logger } from '../../../shared/utils/logger';

interface PortfolioSelectorModalProps {
  visible: boolean;
  onClose: () => void;
  onSelect: (portfolioId: number) => void;
}

export function PortfolioSelectorModal({
  visible,
  onClose,
  onSelect,
}: PortfolioSelectorModalProps) {
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      fetchPortfolios();
    }
  }, [visible]);

  const fetchPortfolios = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authApi.getPortfolios();
      if (response.success && response.portfolios) {
        setPortfolios(response.portfolios);
      } else {
        setError('Failed to load portfolios');
      }
    } catch (err) {
      logger.error('Failed to fetch portfolios for selector', err instanceof Error ? err : undefined, 'portfolio-selector');
      setError('Unable to load portfolios');
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = useCallback((portfolio: Portfolio) => {
    // Only call onSelect - parent will handle closing and state transitions
    // Don't call onClose here as it would reset state (like droppedPin)
    onSelect(portfolio.id);
  }, [onSelect]);

  const renderPortfolio = useCallback(({ item }: { item: Portfolio }) => (
    <Pressable
      style={({ pressed }) => [styles.item, pressed && styles.itemPressed]}
      onPress={() => handleSelect(item)}
      accessibilityRole="button"
      accessibilityLabel={`Select ${item.name}`}
    >
      <Ionicons name="folder-outline" size={icon.md} color={color.textSecondary} />
      <Text style={styles.itemText} numberOfLines={1}>{item.name}</Text>
      <Ionicons name="chevron-forward" size={icon.sm} color={color.textMuted} />
    </Pressable>
  ), [handleSelect]);

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      <View style={styles.container}>
        {/* HEADER */}
        <View style={styles.header}>
          <Pressable onPress={onClose} style={styles.closeButton}>
            <Ionicons name="close" size={icon.lg} color={color.textPrimary} />
          </Pressable>
          <Text style={styles.title}>Select Portfolio</Text>
          <View style={styles.closeButton} />
        </View>

        <Text style={styles.subtitle}>
          Which portfolio should this opportunity be added to?
        </Text>

        {/* CONTENT */}
        {loading ? (
          <View style={styles.center}>
            <ActivityIndicator size="large" color={color.primary} />
          </View>
        ) : error ? (
          <View style={styles.center}>
            <Ionicons name="alert-circle-outline" size={icon.xxl} color={color.error} />
            <Text style={styles.errorText}>{error}</Text>
            <Pressable onPress={fetchPortfolios} style={styles.retryButton}>
              <Text style={styles.retryText}>Retry</Text>
            </Pressable>
          </View>
        ) : portfolios.length === 0 ? (
          <View style={styles.center}>
            <Ionicons name="folder-outline" size={icon.xxl} color={color.textMuted} />
            <Text style={styles.emptyText}>No portfolios yet</Text>
            <Text style={styles.emptySubtext}>Create a portfolio first</Text>
          </View>
        ) : (
          <FlatList
            data={portfolios}
            renderItem={renderPortfolio}
            keyExtractor={item => item.id.toString()}
            contentContainerStyle={styles.list}
          />
        )}
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: color.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: space.md,
    paddingTop: space.lg,
    paddingBottom: space.md,
    borderBottomWidth: 1,
    borderBottomColor: color.border,
  },
  closeButton: {
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: text.cardTitle.fontSize,
    fontWeight: text.cardTitle.fontWeight,
    color: color.textPrimary,
  },
  subtitle: {
    fontSize: text.body.fontSize,
    color: color.textSecondary,
    paddingHorizontal: space.md,
    paddingTop: space.md,
    paddingBottom: space.sm,
  },
  list: {
    padding: space.md,
  },
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: color.surface,
    borderRadius: radius.md,
    padding: space.md,
    marginBottom: space.sm,
    gap: space.sm,
    minHeight: touch.recommended,
  },
  itemPressed: {
    backgroundColor: color.pressed,
  },
  itemText: {
    flex: 1,
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.textPrimary,
  },
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: space.lg,
  },
  errorText: {
    fontSize: text.body.fontSize,
    color: color.error,
    marginTop: space.md,
    textAlign: 'center',
  },
  emptyText: {
    fontSize: text.cardTitle.fontSize,
    fontWeight: text.cardTitle.fontWeight,
    color: color.textPrimary,
    marginTop: space.md,
  },
  emptySubtext: {
    fontSize: text.body.fontSize,
    color: color.textSecondary,
    marginTop: space.xs,
  },
  retryButton: {
    paddingVertical: space.sm,
    paddingHorizontal: space.lg,
    backgroundColor: color.primary,
    borderRadius: radius.md,
    marginTop: space.lg,
  },
  retryText: {
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.surface,
  },
});

